import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, KeyRound, Ban, CheckCircle2 } from "lucide-react";
import { PasswordInput } from "@/components/PasswordInput";
import { PasswordStrength, isStrongPassword } from "@/components/PasswordStrength";

const empty = { email: "", password: "", name: "", role: "Productie", department: "", active: true };

const ROLE_BADGE = {
  "Bedrijfsleiding": "bg-blue-100 text-blue-700",
  "Bedrijfsleiding 2": "bg-indigo-100 text-indigo-700",
  "QA": "bg-violet-100 text-violet-700",
  "Productie": "bg-emerald-100 text-emerald-700",
  "Magazijn": "bg-amber-100 text-amber-700",
  "Expeditie": "bg-orange-100 text-orange-700",
  "TD": "bg-cyan-100 text-cyan-700",
  "Alleen lezen": "bg-slate-100 text-slate-600",
  "Sales": "bg-rose-100 text-rose-700",
};

const fmtDate = (v) => (v ? new Date(v).toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "Nog nooit");

export default function Users() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadRoles = () => api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
  useEffect(() => { load(); loadRoles(); }, []);

  const labelOf = (key) => roles.find((r) => r.key === key)?.label || key;
  const baseOf = (key) => roles.find((r) => r.key === key)?.base || key;

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (u) => { setForm({ ...u, password: "" }); setEditing(u); setOpen(true); };

  const save = async () => {
    if (!form.name || (!editing && (!form.email || !form.password))) return toast.error("Naam, e-mail en wachtwoord zijn verplicht.");
    if (!editing && !isStrongPassword(form.password)) return toast.error("Wachtwoord: minimaal 8 tekens met minstens 1 cijfer.");
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, { name: form.name, role: form.role, department: form.department, active: form.active });
      } else {
        await api.post("/users", { email: form.email.trim().toLowerCase(), password: form.password, name: form.name, role: form.role, department: form.department, active: form.active });
      }
      toast.success("Opgeslagen");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleBlock = async (u) => {
    try { await api.put(`/users/${u.id}`, { active: !u.active }); toast.success(u.active ? "Gebruiker geblokkeerd" : "Gebruiker gedeblokkeerd"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPassword = async () => {
    if (!isStrongPassword(newPw)) return toast.error("Wachtwoord: minimaal 8 tekens met minstens 1 cijfer.");
    try { await api.put(`/users/${pwUser.id}`, { password: newPw }); toast.success("Wachtwoord opnieuw ingesteld"); setPwUser(null); setNewPw(""); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Gebruiker ${u.name} definitief verwijderen?`)) return;
    try { await api.delete(`/users/${u.id}`); load(); toast.success("Verwijderd"); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Gebruikersbeheer</h1>
          <p className="text-sm text-slate-500 mt-1">Beheer gebruikers, rollen, toegang en wachtwoorden. Alleen voor Bedrijfsleiding.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="users-add-button" onClick={openNew} className="bg-[#1C64B0] hover:bg-[#154E8A]"><Plus className="h-4 w-4 mr-2" /> Nieuwe gebruiker</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? "Gebruiker wijzigen" : "Nieuwe gebruiker"}</DialogTitle>
              <DialogDescription>{editing ? "Pas naam, rol, afdeling of toegang aan." : "Voeg een nieuwe medewerker toe met een rol."}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label className="text-xs font-semibold text-slate-600">Naam</Label>
                <Input data-testid="users-field-name" className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label className="text-xs font-semibold text-slate-600">E-mailadres</Label>
                <Input data-testid="users-field-email" name="clazing-new-user-email" autoComplete="off" className="mt-1" disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              {!editing && (
                <div><Label className="text-xs font-semibold text-slate-600">Wachtwoord</Label>
                  <PasswordInput data-testid="users-field-password" name="clazing-new-user-password" autoComplete="new-password" className="mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} toggleTestId="users-field-password-toggle" /><PasswordStrength value={form.password} /></div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs font-semibold text-slate-600">Rol</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="users-field-role" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs font-semibold text-slate-600">Afdeling</Label>
                  <Input data-testid="users-field-department" className="mt-1" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
              </div>
              <div className="flex items-center gap-2">
                <Switch data-testid="users-field-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                <Label className="text-sm text-slate-600">Actief (uit = geblokkeerd)</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuleren</Button>
              <Button data-testid="users-save-button" onClick={save} className="bg-[#1C64B0] hover:bg-[#154E8A]">Opslaan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              {["Naam", "Email", "Rol", "Actief", "Laatste login"].map((h) => (
                <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>
              ))}
              <TableHead className="text-right">Acties</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} className="hover:bg-slate-50" data-testid="users-row">
                <TableCell className="font-semibold text-slate-800">{u.name}</TableCell>
                <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                <TableCell className="text-sm">
                  <span data-testid="users-role-badge" className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-bold ${ROLE_BADGE[baseOf(u.role)] || "bg-slate-100 text-slate-600"}`}>{labelOf(u.role)}</span>
                </TableCell>
                <TableCell>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${u.active ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                    {u.active ? "Actief" : "Geblokkeerd"}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-slate-600 tabular-nums">{fmtDate(u.last_login)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" className="h-8 w-8" title="Wijzigen" data-testid="users-edit" onClick={() => openEdit(u)}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" title="Wachtwoord resetten" data-testid="users-reset-pw" onClick={() => { setPwUser(u); setNewPw(""); }}><KeyRound className="h-4 w-4 text-amber-600" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" title={u.active ? "Blokkeren" : "Deblokkeren"} data-testid="users-block" onClick={() => toggleBlock(u)}>
                    {u.active ? <Ban className="h-4 w-4 text-rose-500" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" title="Verwijderen" data-testid="users-delete" onClick={() => remove(u)}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!pwUser} onOpenChange={(v) => { if (!v) { setPwUser(null); setNewPw(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Wachtwoord resetten</DialogTitle>
            <DialogDescription>Nieuw wachtwoord instellen voor {pwUser?.name}.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs font-semibold text-slate-600">Nieuw wachtwoord</Label>
            <PasswordInput data-testid="users-newpw-field" className="mt-1" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="min. 8 tekens + 1 cijfer" toggleTestId="users-newpw-toggle" /><PasswordStrength value={newPw} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPwUser(null); setNewPw(""); }}>Annuleren</Button>
            <Button data-testid="users-newpw-save" onClick={resetPassword} className="bg-[#1C64B0] hover:bg-[#154E8A]">Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
